import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { PasswordInput } from "../components/shared/PasswordInput";

const ResetPasswordPage = () => {
  const { updatePassword, isRecoveryMode, recoveryError, clearRecoveryMode, isAuthenticated, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    // If there's a recovery error (expired/invalid link), we handle it in the render below
    if (recoveryError) return;

    // Recovery mode already detected by AuthContext
    if (isRecoveryMode) {
      setReady(true);
      return;
    }
    // Fallback: check if there's already a session (e.g., page refresh during recovery)
    if (isAuthenticated) {
      setReady(true);
      return;
    }
    // Listen for late-arriving PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    // Timeout — if no recovery state detected after 4s, show error
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, 4000);
    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [isRecoveryMode, isAuthenticated, recoveryError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const { error } = await updatePassword(password);
    if (error) {
      setError(error.message || 'Failed to update password');
    } else {
      setSuccess(true);
      clearRecoveryMode();
    }
    setLoading(false);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    const { error } = await resetPassword(resendEmail);
    setResendLoading(false);
    if (!error) {
      setResendSent(true);
    }
  };

  // STATE 1: Recovery error (expired or invalid link)
  if (recoveryError || timedOut) {
    const description = recoveryError?.errorDescription || 'This password reset link is invalid or has expired.';
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 data-testid="reset-expired-title">Link Expired</h1>
          <div className="error-message" style={{ marginBottom: 16 }} data-testid="reset-expired-message">
            {decodeURIComponent(description.replace(/\+/g, ' '))}
          </div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: '0.875rem' }}>
            Password reset links expire after a short time for security. Please request a new one.
          </p>
          {resendSent ? (
            <div data-testid="resend-success">
              <div className="success-message" style={{ marginBottom: 16 }}>
                A new reset link has been sent to <strong>{resendEmail}</strong>. Check your inbox.
              </div>
              <Link to="/login" className="btn-primary btn-full" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }} data-testid="back-to-login-link">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleResend} className="auth-form" data-testid="resend-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  data-testid="resend-email-input"
                />
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={resendLoading} data-testid="resend-submit">
                {resendLoading ? 'Sending...' : 'Send New Reset Link'}
              </button>
            </form>
          )}
          <p className="auth-footer" style={{ marginTop: 16 }}>
            <Link to="/login" onClick={() => clearRecoveryMode()}>Back to Sign In</Link>
          </p>
        </div>
      </div>
    );
  }

  // STATE 2: Verifying recovery link (waiting for session)
  if (!ready && !success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>Reset Password</h1>
          <p style={{ color: 'var(--text-muted)' }}>Verifying your reset link...</p>
          <div className="loading" style={{ marginTop: 16 }}>Loading...</div>
        </div>
      </div>
    );
  }

  // STATE 3: Valid recovery session — show password form
  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Set New Password</h1>
        {success ? (
          <div data-testid="reset-success">
            <div className="success-message" style={{ marginBottom: 16 }}>
              Your password has been updated successfully.
            </div>
            <button className="btn-primary btn-full" onClick={async () => { clearRecoveryMode(); await supabase.auth.signOut(); navigate('/login'); }} data-testid="back-to-login">
              Sign In with New Password
            </button>
          </div>
        ) : (
          <>
            <p>Choose a new password for your account.</p>
            {error && <div className="error-message" data-testid="reset-error">{error}</div>}
            <form onSubmit={handleSubmit} className="auth-form" data-testid="reset-form">
              <div className="form-group">
                <label>New Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={loading} data-testid="reset-submit">
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};


export default ResetPasswordPage;
